<template>
    <div class="h-full flex flex-col p-2">
        <div v-show="showAssignmentCreate" class="m-2">
            <create-assignment/>
        </div>
        <div class="flex gap-2">
            <RadioGroup
                v-model="assignmentFilter"
                :items="assignmentFilterRadioGroup"
                orientation="horizontal"
                @change="onAssignmentFilterChange"
            />
            <button class="btn btn-secondary" :disabled="selectedRows.length === 0" @click="onDelete">Slett</button>
            <button class="btn btn-primary" :disabled="selectedRows.length === 0" @click="onComplete">Fullfør</button>
        </div>
        <ag-grid-vue
            class="h-full grow"
            :theme="agTheme"
            :server-side-datasource="taskDataSource"
            :column-defs="colDefs"
            :grid-options="gridOptions"
            row-model-type="serverSide"
            :detail-cell-renderer="ResourceLinksCellRenderer"
            :detail-row-auto-height="true"
            :master-detail="true"
        />
    </div>
</template>

<script setup lang="ts">

import {AgGridVue} from 'ag-grid-vue3'
import {ColDef, GridApi, GridOptions, GridReadyEvent, IServerSideDatasource, IServerSideGetRowsParams, SelectionChangedEvent} from 'ag-grid-community'
import {ASSIGNMENTS_CHANGED_EVENTS, completeAssignment, deleteAssignment, fetchAssignments} from '@/assignments/assignment.service'
import {toQueryRequest} from '@/query/AgGridAdapter'
import {Filters} from '@/query/Filters'
import type {QueryFilter} from '@/query/QueryRequest'
import {formatDateTime} from '@/services/formatter.service'
import ResourceLinksCellRenderer from '@/resource-link/ResourceLinksCellRenderer.vue'
import CreateAssignment from '@/assignments/CreateAssignment.vue'
import {computed, onMounted, onUnmounted, ref, shallowRef} from 'vue'
import {useSingletonEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {agTheme} from '@/agUtils/ag-theme.service'
import RadioGroup from '@nuxt/ui/components/RadioGroup.vue'
import {getEnvironment} from '@/services/infrastructure/envhelper.service'
import {Assignment} from '@/assignments/Assignment.type'


const gridApi = shallowRef<GridApi>()
const selectedRows = ref<Assignment[]>([])
const {onEvents, cleanUp} = useSingletonEmitter('Assignments')
const showAssignmentCreate = computed(() => getEnvironment() !== 'production')

type FilterState = 'all' | 'not_started' | 'completed'
const filterStateMap = new Map<FilterState, Partial<Record<string, QueryFilter>>>([
    ['all', {}],
    ['not_started', { status: Filters.text('pending').equals() }],
    ['completed', { status: Filters.text('completed').equals() }],
])

const assignmentFilterRadioGroup = [
    {
        label: 'Alle',
        description: 'Vis alle oppgaver',
        value: 'all'
    },
    {
        label: 'Ikke påbegynt',
        description: 'Vis ikke påbegynte oppgaver',
        value: 'not_started'
    },
    {
        label: 'Fullført',
        description: 'Vis fullførte oppgaver',
        value: 'completed'
    }
]

const assignmentFilter = ref<FilterState>('not_started')

const onAssignmentFilterChange = () => {
    if (gridApi.value) gridApi.value.refreshServerSide()
}

const onComplete = () => selectedRows.value.forEach((row) => completeAssignment(row.uuid))

const onDelete = () => selectedRows.value.forEach((row) => deleteAssignment(row.uuid))

const taskDataSource: IServerSideDatasource<Assignment> = {
    async getRows(params: IServerSideGetRowsParams<Assignment>) {
        const base = toQueryRequest(params.request)
        const extraFilters = filterStateMap.get(assignmentFilter.value) ?? {}
        const queryRequest = { ...base, filters: { ...extraFilters, ...base.filters } }
        const assignments: Assignment[] = await fetchAssignments(queryRequest)

        if (assignments) {
            params.success({rowData: assignments})
            return
        }
    }
}
// TODO complete button
// TODO a sensible way to show the data in the links
// TODO attach links to new or existing transactions
const colDefs: ColDef<Assignment>[] = [
    {
        headerName: 'Title',
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: 'agGroupCellRenderer'
    },
    {
        headerName: 'Description',
        field: 'description',
    },
    {
        headerName: 'Opprettet',
        field: 'createdAt',
        valueFormatter: (params) => {
            return formatDateTime(params.value)
        },
        initialSort: 'desc'
    },
    {
        headerName: 'Status',
        valueGetter: (params) => {
            if (params.data?.deletedAt) return 'Slettet'
            if (params.data?.status === 'completed') return 'Fullført'
            return 'Ikke påbegynt'
        },
        sortable: false
    }
]

const gridOptions: GridOptions = {
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    rowSelection: {
        mode: 'multiRow', // TODO right most col
        headerCheckbox: false
    },
    onSelectionChanged(event: SelectionChangedEvent<Assignment>) {
        selectedRows.value = event.api.getSelectedRows()
    },
    isRowMaster: (assignment: Assignment) => {
        return assignment.resourceLinks.length > 0
    },
    suppressContextMenu: true,
    enableCellTextSelection: true
}

onMounted(() => {
    onEvents([...ASSIGNMENTS_CHANGED_EVENTS, ORG_SELECTED, ORG_SWITCHED], () => {
        if (gridApi.value) gridApi.value?.refreshServerSide()
    })
})
onUnmounted(cleanUp)

</script>

<style scoped>

</style>